import AsyncStorage from '@react-native-async-storage/async-storage'
import Constants from 'expo-constants'
import { Platform } from 'react-native'
import { SafeJSONObject } from '../constants/Utils'
import { IBuyer } from '../interfaces/buyer'
import { IDevice } from '../interfaces/device'
import cache from '../utils/cache'
import { http } from '../utils/http'
import BaseService from './BaseService'
import DeviceTokenService from './DeviceTokenService'

const deviceTokenService = new DeviceTokenService()

class DeviceService extends BaseService {

  constructor() {
    super('device')
  }

  fetch(): Promise<any> {
    const formData = new FormData()
    formData.append('key', 'version')

    return new Promise((resolve, reject) => {
      const cacheKey = `devices`
      const cachedData = cache.get(cacheKey)

      if (cachedData) resolve(cachedData)
      else {
        try {
          http.post(`${this.model}/read_all_dict`, formData).then(({ data }) => {
            if (data.code === 200) {
              cache.set(cacheKey, data.data)
              resolve(data.data)
            } else reject({ message: data.message })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchFragmentation(): Promise<any> {
    return new Promise((resolve, reject) => {
      const cacheKey = `devices`
      const cachedData = cache.get(cacheKey)

      if (cachedData) resolve(cachedData)
      else {
        try {
          http.get(`${this.model}/fragmentation`).then(({ data }) => {
            if (data.code === 200) {
              const in_zone = data.data['in_zone']
              const out_of_zone = data.data['out_of_zone']

              let in_zone_count = 0
              let out_of_zone_count = 0

              let devices_data: any[] = []

              if (typeof in_zone === 'object') {
                Object.keys(in_zone).forEach((version) => {
                  const version_code = version

                  if (version !== 'unknown') {
                    const version_parts = [
                      version[0],
                      Number(version[1] + version[2]),
                      Number(version[3] + version[4]),
                    ]

                    version = version_parts.join('.')
                  }

                  devices_data.push({
                    name: version,
                    value: Object.keys(in_zone[version_code]).length,
                  })
                })

                devices_data = devices_data.sort((a, b) =>
                  a.name < b.name ? -1 : a.name > b.name ? 1 : 0
                )

                in_zone_count = Number(
                  Object.values(in_zone).reduce(
                    (a: number, version: any): number => {
                      const user_count = Object.keys(version).length
                      return a + user_count
                    },
                    0
                  )
                )
              }

              if (typeof out_of_zone === 'object') {
                out_of_zone_count = Number(Object.keys(out_of_zone).length)
              }

              const final_data = {
                zones: [
                  { name: 'In Zone', value: in_zone_count },
                  { name: 'Out of Zone', value: out_of_zone_count },
                ],
                in_zone_devices: devices_data,
              }

              cache.set(cacheKey, final_data)
              resolve(final_data)
            } else reject({ message: data.message })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchByTag(tag: string): Promise<any> {
    const formData = new FormData()
    formData.append('tag', tag)
    return new Promise((resolve, reject) => {
      try {
        const cacheKey = `device_tag_${tag}`
        const cachedData = cache.get(cacheKey)

        if (cachedData) resolve(cachedData)
        else {
          http.post(`${this.model}/read_by_tag`, formData)
            .then((data) => {
              if (data.code === 200) {
                cache.set(cacheKey, data.data[0])
                resolve(data.data[0])
              } else reject({ message: data.message })
            })
            .catch(({ error }) => {
              reject({ message: 'An unexpected error occured!' })
              throw error
            })
        }
      } catch (error) {
        reject({ message: 'An unexpected error occured!' })
        throw error
      }
    })
  }

  fetchByToken(token: string): Promise<IDevice | null> {
    const formData = new FormData()
    formData.append('token', token)

    return new Promise((resolve, reject) => {
      const cacheKey = `device_by_token_${token}`
      const cachedData = cache.get(cacheKey)

      if (cachedData) resolve(cachedData)
      else {
        try {
          http.post(`${this.model}/read_by_token`, formData)
            .then((data) => {
              if (data.code === 200) {
                if (data.data.length) {
                  cache.set(cacheKey, data.data[0])
                  resolve(data.data[0])
                } else {
                  resolve(null)
                }
              } else reject({ message: data.message })
            })
            .catch(({ error }) => {
              reject({ message: 'An unexpected error occured!' })
              throw error
            })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  registerDevice(): Promise<any> {
    return new Promise(async (resolve, reject) => {

      try {
        // version check/update prompt
        let updateLink: string = ''
        let updateAvailable: boolean = false

        const deviceToken = await deviceTokenService.fetchFcmToken()
        let buyer: IBuyer = SafeJSONObject(await AsyncStorage.getItem('buyerData')) || {}

        if (!deviceToken) {
          reject({ message: 'An unexpected error occurred!' })
          throw new Error('Failed to check update. FCM token not found')
        }

        this.fetchByTag('BUYER_LATEST')
          .then((cloudRefDevice: IDevice) => {
            if (cloudRefDevice) {
              // we found latest version record
              updateLink = cloudRefDevice?.meta

              const cloudRefVersionCode = cloudRefDevice?.version
              const localVersionCode = Constants?.manifest?.extra?.versionCode

              if (cloudRefVersionCode < localVersionCode) {
                updateAvailable = true
              }

              // create device record if no record found
              // update if current version not equal to device version on server
              this.fetchByToken(deviceToken)
                .then((remoteDevice: IDevice | null) => {
                  if (remoteDevice) {
                    const remoteVersionCode = remoteDevice.version
                    if (localVersionCode && localVersionCode != remoteVersionCode) {
                      // update device version on server

                      delete remoteDevice.created
                      delete remoteDevice.modified

                      this.update({
                        ...remoteDevice,
                        version: localVersionCode
                      }).then(() => {
                        resolve([updateLink, updateAvailable])
                      }).catch(error => {
                        reject({ message: 'An unexpected error occurred!' })
                        throw error
                      })
                    }
                    else {
                      resolve([updateLink, updateAvailable])
                    }
                  } else {
                    this.create({
                      tag: 'BUYER',
                      token: deviceToken,
                      meta: JSON.stringify({
                        android_version: Platform.Version,
                        platform: Platform.OS
                      }),
                      user_id: buyer._id,
                      version: localVersionCode
                    }, {
                      resetCache: false
                    }).then(() => {
                      resolve([updateLink, updateAvailable])
                    }).catch(error => {
                      reject({ message: 'An unexpected error occurred!' })
                      throw error
                    })
                  }
                })
                .catch(error => {
                  reject({ message: 'An unexpected error occurred!' })
                  throw error
                })
            } else {
              reject({ message: 'An unexpected error occurred!' })
              throw new Error('BUYER_LATEST device not found.')
            }
          }).catch(error => {
            reject({ message: 'An unexpected error occurred!' })
            throw error
          })
      }
      catch (error) {
        console.log('x-01', error)
        reject({ message: 'An unexpected error occurred!' })
        throw error
      }

    })
  }

}

export default DeviceService
